import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/FirebaseAuthContext';
import Logo from '../../components/ui/Logo';
import FloatingInput from '../../components/ui/FloatingInput';
import { auth } from '../../lib/firebase';
import { browserLocalPersistence, browserSessionPersistence } from 'firebase/auth';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { signIn, signInWithGoogle, resetPassword, userPreferences } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData(prev => ({
      ...prev,
      [e.target.name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const persistence = formData.rememberMe ? browserLocalPersistence : browserSessionPersistence;
      await auth.setPersistence(persistence);
      await signIn(formData.email, formData.password);
      navigate(userPreferences?.hasCompletedOnboarding ? '/dashboard' : '/onboarding/genres');
    } catch (error) {
      setError('Failed to sign in. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
      navigate(userPreferences?.hasCompletedOnboarding ? '/dashboard' : '/onboarding/genres');
    } catch (error) {
      setError('Failed to sign in with Google. Please try again.');
    }
  };

  const handleFacebookSignIn = () => {
    setError('Facebook sign-in is not yet available.');
  };

  return (
    <div className="min-h-screen flex flex-col items-center bg-[#fdfbf5] font-[\'Plus Jakarta Sans\']">
      <div className="mt-8 mb-4">
        <Logo />
      </div>
      <div
        className="w-full max-w-[620px] mx-6 sm:mx-auto rounded-[28px] shadow-[0_4px_32px_0_rgba(0,0,0,0.08)] bg-white p-4 sm:p-8 flex flex-col items-center font-[\'Plus Jakarta Sans\']"
        style={{ marginBottom: '40px' }}
      >
        <h2 className="text-3xl font-bold text-gray-900 mb-2 w-full text-left">Sign in</h2>
        <p className="text-gray-700 mb-6 w-full text-left text-base">
          Don't have an account yet?{' '}
          <Link to="/signup" className="text-[#ff6600] font-semibold hover:underline">Sign up</Link>
        </p>
        <div className="flex w-full gap-3 mb-4">
          <button
            onClick={handleGoogleSignIn}
            type="button"
            className="flex-1 flex items-center justify-center gap-2 border border-gray-200 rounded-full py-2.5 bg-white text-gray-700 font-medium shadow-sm hover:bg-gray-50 transition"
          >
            <img
              className="h-5 w-5"
              src="https://www.svgrepo.com/show/475656/google-color.svg"
              alt="Google logo"
            />
            <span className="text-sm">Sign in with Google</span>
          </button>
          <button
            type="button"
            onClick={handleFacebookSignIn}
            className="flex-1 flex items-center justify-center gap-2 border border-gray-200 rounded-full py-2.5 bg-white text-gray-700 font-medium shadow-sm hover:bg-gray-50 transition"
          >
            <img
              className="h-5 w-5"
              src="https://www.svgrepo.com/show/512120/facebook-176.svg"
              alt="Facebook logo"
            />
            <span className="text-sm">Sign in with Facebook</span>
          </button>
        </div>
        <div className="w-full flex justify-center my-4">
          <span className="text-gray-400 text-sm">- OR -</span>
        </div>
        <form className="w-full flex flex-col items-center" onSubmit={handleSubmit}>
          {error && (
            <div className="text-red-600 text-sm mb-8 w-full max-w-[570px]">{error}</div>
          )}
          {resetSent && (
            <div className="text-green-600 text-sm mb-8 w-full max-w-[570px]">
              Password reset email sent! Please check your inbox.
            </div>
          )}
          <FloatingInput
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Your email"
            autoComplete="email"
          />
          <FloatingInput
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleChange}
            placeholder="Password"
            autoComplete="current-password"
            showPasswordToggle={true}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
          />
          <div className="w-full max-w-[570px] flex justify-between items-center mb-8">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="rememberMe"
                className="rounded border-gray-300 text-[#FF6600] focus:ring-[#FF6600] checked:bg-[#FF6600] checked:border-[#FF6600] bg-white"
                style={{ accentColor: '#FF6600' }}
                checked={formData.rememberMe}
                onChange={handleChange}
              />
              <span className="ml-2 text-sm text-gray-600">Remember me</span>
            </label>
            <button
              type="button"
              onClick={() => navigate('/forgot-password')}
              className="text-sm text-[#333333] font-semibold hover:underline"
            >
              Forgot password?
            </button>
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className="w-full max-w-[570px] py-3 rounded-full bg-[#333333] text-white font-semibold text-base shadow-sm hover:bg-gray-800 transition disabled:opacity-60 font-[\'Plus Jakarta Sans\']"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', backgroundColor: '#333333' }}
          >
            {isLoading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login; 